/* eslint-disable react/prop-types */
import clsx from "clsx";
import React, { FunctionComponent, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { z } from "zod";

import { useForm } from "../../hooks/useForm";
import { DealerVinResultsRoute } from "../../navigation/DealerRoutes";
import { createPathFromEntries } from "../../navigation/createPathFromEntries";
import { getDealerBasePath } from "../../navigation/getDealerBasePath";
import { useRouteParams } from "../../navigation/useRouteParams";
import { useGetVehicleInfo } from "../../queries/useGetVehicleInfo";
import { useSearchForm } from "../../stores/hooks/useSearchForm";
import {
  useVehicleModelIdParam,
  vehicleModelIdParamName,
} from "../../stores/useVehicleModelIdParam";
import { PrimaryButton } from "../../ui/Button/Button";
import { Input } from "../../ui/Input/Input";
import { CarIcon } from "../../ui/icons/CarIcon";
import { tabVehicle } from "./Tabs";
import { MakeInput } from "./inputs/MakeInput";
import { ModelInput } from "./inputs/ModelInput";
import { ProductTypeInput } from "./inputs/ProductTypeInput";
import { TrimInput } from "./inputs/TrimInput";
import { YearInput } from "./inputs/YearInput";

const schema = z.object({
  vehicleChassis: z.string().trim().nonempty({ message: "form.error.vin" }),
});

const VIN_LENGTH = 17;

export const Vin: FunctionComponent = () => {
  const { t } = useTranslation();
  const params = useRouteParams(DealerVinResultsRoute);
  const suppressAutoDecodeRef = useRef(false);
  const vinEditedRef = useRef(false);
  const [
    {
      productType,
      vehicleChassis,
      vehicleManufacturerName,
      vehicleModelClassId,
      vehicleModelName,
      vehicleSubmodel,
      vehicleSubmodelTiresizeId,
      vehicleYear,
    },
    { setActiveTab, setDecodedVehicle, setVehicleChassis },
  ] = useSearchForm();
  const { data: vehicleInfo } = useGetVehicleInfo();
  const [selectedVehicleModelId, setSelectedVehicleModelId] =
    useVehicleModelIdParam();
  const [, setLocation] = useLocation();
  const decodedVehicle = vehicleInfo?.decodedVehicle;
  const modelCandidates = vehicleInfo?.modelCandidates ?? [];
  const routeVin = params?.vehicleVin || "";
  const currentVin = vehicleChassis ?? routeVin;
  const isCurrentVinDecoded = Boolean(
    decodedVehicle && routeVin && currentVin === routeVin
  );
  const canSubmitVehicleSearch = Boolean(
    isCurrentVinDecoded &&
      vehicleSubmodel &&
      vehicleYear &&
      vehicleManufacturerName &&
      vehicleModelName &&
      productType
  );

  // The route VIN is authoritative: a VIN left in the search form by a previous
  // search (the form state outlives the route) must not survive navigation to a
  // different vin-search URL.
  useEffect(() => {
    if (routeVin && vehicleChassis !== routeVin) {
      vinEditedRef.current = false;
      setVehicleChassis(routeVin);
    }
  }, [routeVin]);

  useEffect(() => {
    if (!decodedVehicle) {
      return;
    }

    const decodedVehicleYear = decodedVehicle.year
      ? String(decodedVehicle.year)
      : null;
    const decodedVehicleMake = decodedVehicle.make || null;
    const matchedModelName =
      modelCandidates.length <= 1
        ? decodedVehicle.matchedModelName || decodedVehicle.model
        : null;

    if (
      selectedVehicleModelId &&
      vehicleYear === decodedVehicleYear &&
      vehicleManufacturerName === decodedVehicleMake &&
      vehicleModelName
    ) {
      return;
    }

    if (
      modelCandidates.length > 1 &&
      vehicleYear === decodedVehicleYear &&
      vehicleManufacturerName === decodedVehicleMake &&
      vehicleModelName
    ) {
      return;
    }

    if (
      vehicleYear === decodedVehicleYear &&
      vehicleManufacturerName === decodedVehicleMake &&
      vehicleModelName === matchedModelName
    ) {
      return;
    }

    setDecodedVehicle(decodedVehicleYear, decodedVehicleMake, matchedModelName);
  }, [
    decodedVehicle?.year,
    decodedVehicle?.make,
    decodedVehicle?.model,
    decodedVehicle?.matchedModelName,
    modelCandidates.length,
    selectedVehicleModelId,
    vehicleManufacturerName,
    vehicleModelName,
    vehicleYear,
  ]);

  useEffect(() => {
    const normalizedVin = currentVin.trim();
    const basePath = getDealerBasePath();

    if (
      !basePath ||
      suppressAutoDecodeRef.current ||
      normalizedVin.length !== VIN_LENGTH ||
      normalizedVin === routeVin ||
      // A VIN we have not caught up with the route on yet is stale, not typed.
      (!!routeVin && !vinEditedRef.current)
    ) {
      return;
    }

    setLocation(
      `${basePath}/product/TIRES/type/vin-search/vin/${encodeURIComponent(
        normalizedVin
      )}`
    );
  }, [currentVin, routeVin]);

  const modelCandidateOptions = useMemo(
    () =>
      modelCandidates.map(({ vehicleModelId, vehicleModelName }) => ({
        data: {
          vehicleModelClassId: "",
          vehicleModelClassName: { en: "", fr: "" },
          vehicleModelId: String(vehicleModelId),
          vehicleModelImage: "",
          vehicleModelName: {
            en: vehicleModelName,
            fr: vehicleModelName,
          },
          vehicleModelSortOrder: "",
        },
        label: vehicleModelName,
        value: String(vehicleModelId),
      })),
    [modelCandidates]
  );

  const [handleForm] = useForm({
    noReset: true,
    onSubmit() {
      const basePath = getDealerBasePath();
      if (!basePath) {
        return;
      }

      if (!canSubmitVehicleSearch) {
        const normalizedVin = currentVin.trim();
        if (normalizedVin.length === VIN_LENGTH) {
          setLocation(
            `${basePath}/product/TIRES/type/vin-search/vin/${encodeURIComponent(
              normalizedVin
            )}`
          );
        }
        return;
      }

      const nextSearchParams = new URLSearchParams();
      if (vehicleSubmodelTiresizeId) {
        nextSearchParams.set("size", vehicleSubmodelTiresizeId);
      }
      if (currentVin) {
        nextSearchParams.set("vin", currentVin);
      }
      if (selectedVehicleModelId) {
        nextSearchParams.set(vehicleModelIdParamName, selectedVehicleModelId);
      }
      const nextQueryString = nextSearchParams.toString();

      const nextPath = [
        createPathFromEntries(
          [
            ["product", productType ?? undefined],
            ["type", "vehicle-search"],
            ["year", vehicleYear ?? undefined],
            ["make", vehicleManufacturerName ?? undefined],
            ["category", vehicleModelClassId || "0"],
            ["model", vehicleModelName ?? undefined],
            ["trim", vehicleSubmodel ?? undefined],
          ],
          basePath
        ),
        nextQueryString,
      ]
        .filter(Boolean)
        .join("?");

      suppressAutoDecodeRef.current = true;
      setLocation(nextPath);
      setActiveTab(tabVehicle);
    },
    schema,
  });

  return (
    <form
      className={clsx("grid sm:grid-cols-2 md:grid-cols-3 xl:flex gap-2")}
      noValidate
      onSubmit={handleForm}
    >
      <Input
        onChange={(event): void => {
          vinEditedRef.current = true;
          setVehicleChassis(event.target.value.trim().toUpperCase());
        }}
        aria-label="selectedVin"
        before={<CarIcon />}
        name="vehicleChassis"
        placeholder={t("searchForm.vinPlaceholder")}
        value={currentVin}
      />
      {isCurrentVinDecoded && (
        <>
          <YearInput />
          <MakeInput />
          <ModelInput
            extraOptions={modelCandidateOptions}
            onSelectModelId={setSelectedVehicleModelId}
          />
          <TrimInput />
          <ProductTypeInput />
        </>
      )}
      <PrimaryButton
        aria-label="viewResults"
        className="shrink-0 shadow-lg h-12 md:h-14"
        disabled={!canSubmitVehicleSearch}
        type="submit"
      >
        {t("searchForm.viewResults")}
      </PrimaryButton>
    </form>
  );
};

/**
 * @see docs/worklog/TW-967-hunter-vin-results-vehicle-tire-sizes/rca.md
 * @see docs/worklog/TW-967-hunter-vin-results-vehicle-tire-sizes/rfc.md
 * @see docs/worklog/TW-967-hunter-vin-results-vehicle-tire-sizes/tfc.md
 */
